import { FC } from 'react';
import { Box, Link, Typography } from '@mui/material';
import type { UseOtpResendResult } from 'src/types/otpResend';

interface OtpResendBlockProps {
  resend: UseOtpResendResult;
  resendLabel?: string;
  inline?: boolean;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const OtpResendBlock: FC<OtpResendBlockProps> = ({
  resend,
  resendLabel = "Didn't get a code?",
  inline = false,
}) => {
  const {
    canResend,
    remainingSeconds,
    isExhausted,
    isSending,
    error,
    supportContact,
  } = resend;

  if (isExhausted) {
    if (supportContact) {
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Code limit reached.{' '}
            {supportContact.email && (
              <>
                Email{' '}
                <Link href={`mailto:${supportContact.email}`}>
                  {supportContact.label}
                </Link>{' '}
                for support.
              </>
            )}
            {!supportContact.email && supportContact.phone && (
              <>
                {' '}
                Call{' '}
                <Link href={`tel:${supportContact.phone}`}>
                  {supportContact.label}
                </Link>{' '}
                for support.
              </>
            )}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary">
        Code limit reached. Please contact Pronto for support.
      </Typography>
    );
  }

  return (
    // Column layout: the label + Resend link form the control row, and any
    // error sits on its own line *below* it rather than being squeezed in
    // beside the link (which wrapped awkwardly in the compliance contact row).
    <Box
      sx={{
        display: inline ? 'inline-flex' : 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.25,
        // Inline use (compliance contact row) shares a flex row with the
        // contact value + Cancel, so cap the block width to stop a long
        // error message from squeezing them.
        maxWidth: inline ? '38ch' : undefined,
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" component="span">
          {isSending ? 'Sending\u2026' : resendLabel}{' '}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="always"
          color={canResend && !isSending ? 'primary' : 'text.disabled'}
          onClick={() => canResend && !isSending && void resend.send()}
          aria-disabled={!canResend || isSending}
          aria-live="polite"
        >
          {isSending
            ? ''
            : remainingSeconds > 0
            ? `Resend in ${formatCountdown(remainingSeconds)}`
            : 'Resend'}
        </Link>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default OtpResendBlock;
